import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  UsersIcon,
  ClockIcon,
  MapPinIcon,
  EyeIcon,
  UserPlusIcon,
  CreditCardIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  ShoppingBagIcon
} from '@heroicons/react/24/outline';

const GroupOrderCard = ({ 
  order, 
  onJoin, 
  onView, 
  onPay, 
  showActions = true, 
  isUserOrder = false,
  currentUser = null 
}) => {
  const navigate = useNavigate();
  const getTimeLeft = (deadline) => {
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate - now;
    
    if (diff <= 0) return { text: 'Expired', urgent: true };
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return { text: `${days}d ${hours % 24}h left`, urgent: false };
    }
    
    return { text: `${hours}h ${minutes}m left`, urgent: hours < 2 };
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'closed':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ordered':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'delivered':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getCurrentParticipant = () => {
    if (!currentUser || !order.participants) return null;
    return order.participants.find(p => 
      p.user._id === currentUser._id || p.user.username === currentUser.username
    );
  };

  const isCreator = () => {
    return currentUser && order.creator?._id === currentUser._id;
  };

  const canJoin = () => {
    return !isUserOrder && 
           order.status === 'open' && 
           (order.currentParticipants || order.participants?.length || 0) < order.maxParticipants &&
           !getCurrentParticipant();
  };

  const canPay = () => {
    const participant = getCurrentParticipant();
    const userParticipation = order.userParticipation;
    
    return isUserOrder && 
           order.status === 'open' && 
           (participant || userParticipation) && 
           !(userParticipation?.hasPaid || participant?.hasPaid) && 
           (userParticipation?.subtotal || participant?.subtotal || 0) > 0 &&
           // Ensure user has selected meals before allowing payment
           ((participant?.meals && participant.meals.length > 0) || 
            (userParticipation?.mealsCount && userParticipation.mealsCount > 0));
  };

  const canSelectMeals = () => {
    const participant = getCurrentParticipant();
    const userParticipation = order.userParticipation;
    
    return isUserOrder && 
           order.status === 'open' && 
           (participant || userParticipation) && 
           !(userParticipation?.hasPaid || participant?.hasPaid) &&
           // Show if user hasn't selected meals yet or has no subtotal
           (!participant?.meals || participant.meals.length === 0 || 
            (userParticipation?.subtotal || participant?.subtotal || 0) === 0);
  };

  const timeLeft = getTimeLeft(order.orderDeadline);
  const participantCount = order.currentParticipants || order.participants?.length || 0;
  const currentParticipant = getCurrentParticipant();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-accentwhite rounded-2xl shadow-card border-2 p-6 hover:shadow-lg transition-all duration-300 ${
        isUserOrder && currentParticipant ? 'border-pepperoni/30' : 'border-secondary-200'
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-lg font-bold text-crust font-display">
              {order.title}
            </h3>
            {isCreator() && (
              <span className="text-xs bg-cheese text-crust px-2 py-1 rounded-full font-bold">
                Creator
              </span>
            )}
            {/* Show role from API response for user orders */}
            {isUserOrder && order.userRole && (
              <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                order.userRole === 'creator' 
                  ? 'bg-cheese text-crust' 
                  : 'bg-secondary-200 text-olive-light'
              }`}>
                {order.userRole === 'creator' ? 'Creator' : 'Participant'}
              </span>
            )}
          </div>
          <p className="text-sm text-olive-light font-sans">
            by @{order.creator?.username}
          </p>
          {order.restaurant && (
            <p className="text-sm font-bold text-pepperoni">
              {order.restaurant.name}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          <span className={`px-3 py-1 rounded-full text-xs font-bold font-display border ${getStatusColor(order.status)}`}>
            {order.status.toUpperCase()}
          </span>
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
            timeLeft.urgent ? 'bg-red-100 text-red-800' : 'bg-primary-50 text-pepperoni'
          }`}>
            {timeLeft.text}
          </span>
        </div>
      </div>

      {/* Description */}
      {order.description && (
        <p className="text-sm text-olive-light font-sans mb-4 line-clamp-2">
          {order.description}
        </p>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-secondary-50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <UsersIcon className="w-4 h-4 text-pepperoni" />
            <span className="text-xs text-olive-light font-sans">Participants</span>
          </div>
          <div className="font-bold text-crust font-display">
            {participantCount}/{order.maxParticipants}
          </div>
        </div>
        
        <div className="bg-secondary-50 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <CreditCardIcon className="w-4 h-4 text-pepperoni" />
            <span className="text-xs text-olive-light font-sans">Total Amount</span>
          </div>
          <div className="font-bold text-crust font-display">₦{order.totalAmount || 0}</div>
        </div>
      </div>

      {/* User-specific info for user orders */}
      {isUserOrder && (order.userParticipation || currentParticipant) && (
        <div className="bg-primary-50 rounded-xl p-3 mb-4 border border-pepperoni/20">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xs text-olive-light font-sans">Your Share</div>
              <div className="font-bold text-pepperoni font-display">
                ₦{order.userParticipation?.subtotal || currentParticipant?.subtotal || 0}
              </div>
              <div className="text-xs text-olive-light font-sans">
                {order.userParticipation?.mealsCount || currentParticipant?.meals?.length || 0} items
              </div>
              {order.userParticipation?.joinedAt && (
                <div className="text-xs text-olive-light font-sans">
                  Joined: {new Date(order.userParticipation.joinedAt).toLocaleDateString()}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className={`text-xs font-bold ${
                (order.userParticipation?.hasPaid || currentParticipant?.hasPaid) ? 'text-green-600' : 'text-red-600'
              }`}>
                {(order.userParticipation?.hasPaid || currentParticipant?.hasPaid) ? '✅ Paid' : '⏳ Pending'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Minimum order warning */}
      {order.minOrderAmount > 0 && order.totalAmount < order.minOrderAmount && (
        <div className="bg-yellow-50 rounded-xl p-3 mb-4 border border-yellow-200">
          <div className="flex items-center gap-2">
            <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />
            <div className="text-xs text-yellow-800 font-sans">
              Need ₦{order.minOrderAmount - order.totalAmount} more to reach minimum order
            </div>
          </div>
        </div>
      )}

      {/* Delivery Info */}
      <div className="bg-secondary-50 rounded-xl p-3 mb-4">
        <div className="flex items-center gap-2 mb-1">
          <MapPinIcon className="w-4 h-4 text-pepperoni" />
          <span className="text-xs text-olive-light font-sans">Delivery Location</span>
        </div>
        <div className="text-sm font-bold text-crust">
          {order.deliveryInfo?.location}
        </div>
        {order.deliveryTime && (
          <div className="text-xs text-olive-light font-sans mt-1">
            Delivery: {new Date(order.deliveryTime).toLocaleString()}
          </div>
        )}
      </div>

      {/* Actions */}
      {showActions && (
        <div className="flex gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onView(order._id)}
            className="flex-1 py-2 px-4 border-2 border-secondary-300 text-olive-light rounded-xl font-bold font-display text-sm hover:bg-secondary-50 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <EyeIcon className="w-4 h-4" />
            View
          </motion.button>

          {canJoin() && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onJoin(order._id)}
              className="flex-1 py-2 px-4 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite rounded-xl font-bold font-display text-sm shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <UserPlusIcon className="w-4 h-4" />
              Join
            </motion.button>
          )}

          {canSelectMeals() && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(`/user/group-orders/${order._id}/meals`)}
              className="flex-1 py-2 px-4 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite rounded-xl font-bold font-display text-sm shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <ShoppingBagIcon className="w-4 h-4" />
              Select Meals
            </motion.button>
          )}

          {canPay() && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => onPay(order._id)}
              className="flex-1 py-2 px-4 bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-600 text-white rounded-xl font-bold font-display text-sm shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
            >
              <CreditCardIcon className="w-4 h-4" />
              Pay
            </motion.button>
          )}

          {participantCount >= order.maxParticipants && !isUserOrder && (
            <div className="flex-1 py-2 px-4 bg-gray-300 text-gray-500 rounded-xl font-bold font-display text-sm text-center">
              Full
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

export default GroupOrderCard;